// Update App.tsx to use enhanced error boundaries
import React, { useEffect, useCallback, useState, useRef, memo } from 'react';
import { Loader2, AlertTriangle, Search, Users, BookOpen, ChevronRight, List } from 'lucide-react';
import { AppProvider } from './context/AppProvider';
import { useAppContext } from './hooks/useAppContext';
import { useApi } from './hooks/useApi';
import { useDebounce } from './hooks/useDebounce';
import { useFirebaseAuth } from './hooks/useFirebaseAuth';
import { useAuth } from './hooks/useAuth';
import { useBilling } from './hooks/useBilling';
import { API_BASE_URL } from './constants';
import NotificationCenter from './components/NotificationCenter';
import ConfirmationDialog from './components/ConfirmationDialog';
import ListSelectionModal from './components/ListSelectionModal';
import FilterSidebar from './components/FilterSidebar';
import DataTable from './components/DataTable';
import Pagination from './components/Pagination';
import TopNav from './components/TopNav';
import Terms from './components/Terms';
import PrivacyPolicy from "./components/PrivacyPolicy";
import LoginPage from "./components/LoginPage";
import Activity from "./components/Analytics";
import AdminPage from "./components/AdminPage";

import ErrorBoundary from "./components/ErrorBoundary";
import AccountPage from './components/AccountPage';
import { DEBOUNCE_DELAY } from "./constants";
import type { RateLimitInfo, CircuitBreakerState } from './types';
import icon from "./assets/icon.png";
import AuthProvider from './context/AuthContext';


interface UserList {
  list_id: string;
  name: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
  contact_count: number;
}

// OPTIMIZED: Memoized loading component
const LoadingState = memo(() => (
  <div className="app-loading">
    <div className="loading-content">
      <div className="logo">
        <img 
          src={icon} 
          alt="App Icon" 
          className="logo-icon" 
          style={{ 
            width: 48, 
            height: 48, 
            objectFit: 'contain', 
            borderRadius: 0, 
            boxShadow: 'none', 
            padding: 0, 
            display: 'block' 
          }} 
        />
        <div className="logo-text" style={{ fontFamily: 'Avenir, Inter, -apple-system, sans-serif' }}>
          ppl.contact
        </div>
      </div>
      {/* Only one spinner below the logo */}
      <Loader2 size={32} className="spinning" />
    </div>
  </div>
));

LoadingState.displayName = 'LoadingState';

// FIXED: Properly typed error state component
const ErrorState = memo(({ 
  error, 
  schemaError, 
  apiKey, 
  rateLimitInfo, 
  circuitBreakerState 
}: {
  error?: string | null;
  schemaError?: string | null;
  apiKey?: string | null;
  rateLimitInfo?: RateLimitInfo | null;
  circuitBreakerState?: CircuitBreakerState | null;
}) => {
  const isAuthError = schemaError?.includes('authentication') || error?.includes('authentication');
  const isRateLimitError = schemaError?.includes('Rate limit') || error?.includes('Rate limit');
  const isCircuitBreakerError = schemaError?.includes('circuit breaker') || error?.includes('circuit breaker');

  return (
    <div className="error-page">
      <div className="error-content">
        <AlertTriangle size={64} />
        <h2>
          {isAuthError ? 'API Authentication Error' : 
           isRateLimitError ? 'Rate Limit Exceeded' : 
           isCircuitBreakerError ? 'Service Protection Active' : 
           schemaError ? 'Schema Configuration Error' : 
           'API Server Required'}
        </h2>
        {isAuthError ? (
          <div>
            <p>API authentication failed. Please verify:</p>
            <ul style={{ textAlign: 'left', marginTop: 16 }}>
              <li><strong>API Token:</strong> {apiKey}</li>
              <li><strong>API Server:</strong> {API_BASE_URL}</li>
              <li><strong>Permissions:</strong> Token has search and metadata access</li>
            </ul>
            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              background: '#f8f9fa', 
              borderRadius: 8, 
              fontSize: 14, 
              color: '#666' 
            }}>
              <strong>Error:</strong> {schemaError || error}
            </div>
          </div>
        ) : isRateLimitError ? (
          <div style={{ textAlign: 'center' }}>
            <p>API rate limit exceeded. The system will automatically retry.</p>
            {rateLimitInfo && (
              <div style={{ 
                marginTop: 16, 
                padding: 12, 
                background: '#fef3c7', 
                borderRadius: 8, 
                fontSize: 14 
              }}>
                <strong>Rate Limit Status:</strong> {rateLimitInfo.remaining}/{rateLimitInfo.limit} remaining<br />
                <strong>Reset Time:</strong> {new Date(rateLimitInfo.reset).toLocaleTimeString()}
              </div>
            )}
          </div>
        ) : isCircuitBreakerError ? (
          <div style={{ textAlign: 'center' }}>
            <p>Service protection is active due to repeated failures. The system will automatically retry when the service recovers.</p>
            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              background: '#fef3c7', 
              borderRadius: 8, 
              fontSize: 14 
            }}>
              <strong>Protection Status:</strong> {circuitBreakerState?.state}<br />
              <strong>Failure Count:</strong> {circuitBreakerState?.failureCount}
            </div>
          </div>
        ) : (
          <p>
            {schemaError ? `Schema configuration failed: ${schemaError}` : 
             'Please start the API server with authentication enabled:'}
          </p>
        )}
        {!schemaError && !isAuthError && !isRateLimitError && !isCircuitBreakerError && (
          <pre>python main.py</pre>
        )}
        <div className="error-actions">
          <button onClick={() => window.location.reload()} className="button-primary">
            Retry Connection
          </button>
        </div>
      </div>
    </div>
  );
});

ErrorState.displayName = 'ErrorState';

const AppContent: React.FC = memo(() => {
  const [listPickerLists, setListPickerLists] = useState<UserList[]>([]);
  const [listPickerLoading, setListPickerLoading] = useState(false);
  const [listSelectionModalOpen, setListSelectionModalOpen] = useState(false);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);

  const {
    state,
    dispatch,
    showToast,
    setNavigation,
    setView,
    unlockSelectedContacts,
    addSelectedContactsToList,
    deleteContactsJobAsync,
    removeFromListJobAsync,
    exportSelectedContacts,
    deleteList
  } = useAppContext();
  
  const { isAuthenticated } = useFirebaseAuth();
  const { apiKey, hasPermission, user: authUser } = useAuth();

  const {
    searchContacts,
    checkApiHealth,
    getSchema,
    loadInitialMetadata,
    loadDataSourceCount,
    refreshMetadata,
    getLists,
    requestTrackerState,
    rateLimitInfo,
    circuitBreakerState
  } = useApi();

  // ENHANCED: Add billing hook to ensure billing balance is available for payment banner
  useBilling();

  const debouncedQuery = useDebounce(state.searchState.query, DEBOUNCE_DELAY);

  // OPTIMIZED: Memoized computed values that are actually used
  const selectedCount = state.selection.selectedIds.size;
  const currentDataSource = state.searchState.dataSource;
  const allSelected = state.selection.selectAll;

  // OPTIMIZED: Stable refs to prevent unnecessary re-renders
  const initializationStarted = useRef(false);
  const isInitialLoad = useRef(true);
  const lastSearchParams = useRef<string>('');
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastNavigationState = useRef(state.navigationState);
  const countsLoaded = useRef(false);
  const backgroundCountsLoading = useRef(false);

  // OPTIMIZED: Memoized navigation change handler
  const handleNavigationChange = useCallback(() => {
    if (state.initialized && state.navigationState !== lastNavigationState.current) {
      console.log('🔄 Navigation state changed, refreshing metadata.', state.navigationState);
      const { view, listId } = state.navigationState;
      const dataSource = view === 'people' ? 'people' : 'contacts';
      const filters = listId ? { 
        list_ids: { type: 'picklist', values: [listId], operator: 'OR' } 
      } : {};

      refreshMetadata(undefined, filters, undefined, dataSource);
      lastNavigationState.current = state.navigationState;
    }
  }, [state.initialized, state.navigationState, refreshMetadata]);

  useEffect(() => {
    handleNavigationChange();
  }, [handleNavigationChange]);

  // OPTIMIZED: App initialization with better error handling
  const initializeApp = useCallback(async () => {
    console.log(`🔧 initializeApp called - initializationStarted: ${initializationStarted.current}, initialized: ${state.initialized}, initializing: ${state.initializing}`);
    if (initializationStarted.current || state.initialized || state.initializing) {
      console.log('⏭️ Skipping initialization - already in progress or completed');
      return;
    }

    console.log('🚀 Starting initialization process...');
    initializationStarted.current = true;
    dispatch({ type: 'SET_INITIALIZING', payload: true });

    try {
      if (!circuitBreakerState.canRequest) {
        throw new Error(`Service temporarily unavailable (circuit breaker ${circuitBreakerState.state})`);
      }

      if (rateLimitInfo && rateLimitInfo.remaining === 0) {
        const waitTime = Math.max(0, rateLimitInfo.reset - Date.now());
        if (waitTime > 0) {
          showToast({ 
            type: 'info', 
            title: 'Rate Limit', 
            message: `Waiting ${Math.round(waitTime / 1000)}s...`, 
            duration: 5000 
          });
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }

      const isHealthy = await checkApiHealth();
      if (!isHealthy) {
        throw new Error('Unable to connect to API server or authentication failed');
      }
      dispatch({ type: 'SET_API_CONNECTED', payload: true });

      dispatch({ type: 'SET_SCHEMA_LOADING', payload: true });
      const schema = await getSchema();

      if (!schema) {
        throw new Error('Failed to load schema configuration - check API authentication');
      }

      dispatch({ type: 'SET_SCHEMA', payload: schema });
      
      // Load metadata in background - don't block initialization
      loadInitialMetadata().catch(error => {
        console.warn('⚠️ Metadata loading failed, continuing with initialization:', error);
      });
      
      console.log('✅ Setting initialized to true');
      dispatch({ type: 'SET_INITIALIZED', payload: true });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize application';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      dispatch({ type: 'SET_API_CONNECTED', payload: false });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      initializationStarted.current = false;

      if (errorMessage.includes('schema')) {
        dispatch({ type: 'SET_SCHEMA_ERROR', payload: errorMessage });
      }

      // OPTIMIZED: Better error handling with specific recovery strategies
      if (errorMessage.includes('circuit breaker')) {
        showToast({ 
          type: 'error', 
          title: 'Service Unavailable', 
          message: 'API service is temporarily unavailable. Will retry automatically...', 
          duration: 10000 
        });
        retryTimeoutRef.current = setTimeout(() => {
          initializationStarted.current = false;
          initializeApp();
        }, 30000);
      } else if (errorMessage.includes('authentication') || errorMessage.includes('Authentication')) {
        showToast({ 
          type: 'error', 
          title: 'Authentication Failed', 
          message: 'API authentication failed. Check API key.', 
          duration: 10000 
        });
      } else if (errorMessage.includes('Rate limit')) {
        showToast({ 
          type: 'warning', 
          title: 'Rate Limited', 
          message: 'API rate limit exceeded. Will retry automatically...', 
          duration: 8080 
        });
        retryTimeoutRef.current = setTimeout(() => {
          initializationStarted.current = false;
          initializeApp();
        }, 10000);
      } else {
        showToast({ 
          type: 'error', 
          title: 'Initialization Failed', 
          message: errorMessage, 
          duration: 7000 
        });
      }
    }
  }, [
    checkApiHealth, 
    getSchema, 
    loadInitialMetadata, 
    dispatch, 
    showToast,
    state.initialized, 
    state.initializing, 
    circuitBreakerState, 
    rateLimitInfo
  ]);

  // OPTIMIZED: Background counts loading with better performance
  const loadBackgroundCounts = useCallback(async () => {
    if (countsLoaded.current || !state.initialized || backgroundCountsLoading.current) return;

    try {
      backgroundCountsLoading.current = true;

      if (requestTrackerState.isSearching) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      const [peopleCount, contactsCount] = await Promise.allSettled([
        loadDataSourceCount('people'),
        loadDataSourceCount('contacts')
      ]);

      if (peopleCount.status === 'fulfilled' && peopleCount.value !== null) {
        dispatch({ 
          type: 'SET_DATA_SOURCE_COUNT', 
          payload: { dataSource: 'people', count: peopleCount.value } 
        });
      }
      
      if (contactsCount.status === 'fulfilled' && contactsCount.value !== null) {
        dispatch({ 
          type: 'SET_DATA_SOURCE_COUNT', 
          payload: { dataSource: 'contacts', count: contactsCount.value } 
        });
      }

      countsLoaded.current = true;
    } catch (error) {
      console.error('❌ Failed to load background counts:', error);
    } finally {
      backgroundCountsLoading.current = false;
    }
  }, [state.initialized, loadDataSourceCount, dispatch, requestTrackerState.isSearching]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Initialize app only when API key is available
  useEffect(() => {
    console.log(`🔧 Initialization check - apiKey: ${apiKey ? 'present' : 'null'}, initialized: ${state.initialized}, initializing: ${state.initializing}`);
    if (apiKey && !state.initialized && !state.initializing) {
      console.log('🚀 Starting app initialization...');
      initializeApp();
    }
  }, [initializeApp, apiKey, state.initialized, state.initializing]);

  // Load background counts
  useEffect(() => {
    if (state.initialized && !countsLoaded.current && !backgroundCountsLoading.current) {
      setTimeout(() => loadBackgroundCounts(), 100);
    }
  }, [state.initialized, loadBackgroundCounts]);

  // OPTIMIZED: Search function with better performance
  const performSearch = useCallback(async () => {
    if (!state.initialized || !state.schema) return;

    const searchParamsString = JSON.stringify({
      searchState: state.searchState,
      navigationState: state.navigationState
    });

    if (searchParamsString === lastSearchParams.current) {
      return;
    }
    lastSearchParams.current = searchParamsString;

    if (isInitialLoad.current) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    if (rateLimitInfo && rateLimitInfo.remaining < 2) {
      const waitTime = Math.max(0, rateLimitInfo.reset - Date.now());
      if (waitTime > 0) {
        await new Promise(resolve => setTimeout(resolve, Math.min(waitTime, 5000)));
      }
    }

    try {
      const completeFilters = { ...state.searchState.filters };
      if (state.navigationState.view === 'list' && state.navigationState.listId) {
        completeFilters.list_ids = { 
          type: 'picklist', 
          values: [state.navigationState.listId], 
          operator: 'OR' 
        };
      }

      const result = await searchContacts(
        { ...state.searchState, query: debouncedQuery, filters: completeFilters }, 
        state.pageSize, 
        state.schema
      );

      if (result) {
        const { response } = result;
        dispatch({
          type: 'SET_SEARCH_RESULTS',
          payload: {
            contacts: response.hits,
            total: response.total,
            totalPages: response.total_pages,
            page: response.page,
            searchTime: response.query_time_ms,
            dataSource: state.searchState.dataSource
          }
        });

        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Search failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        dispatch({ type: 'SET_LOADING', payload: false });
      }
      // Don't show toast for API key or search errors - handle them silently
    }
  }, [
    debouncedQuery, 
    state.searchState, 
    state.navigationState, 
    state.initialized, 
    state.schema, 
    state.pageSize, 
    searchContacts, 
    dispatch, 
    showToast, 
    rateLimitInfo
  ]);

  // Perform search when dependencies change
  useEffect(() => {
    if (state.initialized && state.schema) {
      performSearch();
    }
  }, [state.initialized, state.schema, performSearch]);

  // OPTIMIZED: List management with better error handling
  const refreshLists = useCallback(async () => {
    if (currentDataSource !== 'contacts') return;

    setListPickerLoading(true);
    try {
      const data = await getLists(false);
      setListPickerLists(data && typeof data === 'object' && 'success' in data && data.success ? (data as { success: boolean; lists?: UserList[] }).lists || [] : []);
    } catch (error) {
      console.error('Failed to refresh lists:', error);
      setListPickerLists([]);
      showToast({
        type: 'error',
        title: 'List Loading Failed',
        message: 'Failed to load contact lists'
      });
    } finally {
      setListPickerLoading(false);
    }
  }, [currentDataSource, getLists, showToast]);

  // Refresh lists when data source changes to contacts
  useEffect(() => {
    if (currentDataSource === 'contacts') {
      refreshLists();
    }
  }, [currentDataSource, refreshLists]);

  // Listen for list refresh events
  useEffect(() => {
    const handleListsRefresh = () => {
      if (currentDataSource === 'contacts') {
        refreshLists();
      }
    };

    window.addEventListener('lists-refresh', handleListsRefresh);
    return () => window.removeEventListener('lists-refresh', handleListsRefresh);
  }, [currentDataSource, refreshLists]);

  // OPTIMIZED: Memoized computed states
  const showSearchLoading = requestTrackerState.isSearching && !isInitialLoad.current;

  // OPTIMIZED: Memoized view renderer
  const renderView = useCallback(() => {
    console.log(`🔍 renderView called - currentView: ${state.currentView}, dataSource: ${state.searchState.dataSource}`);
    
    switch (state.currentView) {
      case 'main':
      default:
        return (
          <>
            <ErrorBoundary>
              <FilterSidebar 
                isOpen={filterSidebarOpen} 
                onToggle={() => setFilterSidebarOpen(!filterSidebarOpen)} 
              />
            </ErrorBoundary>
            <div 
              className={`main-content ${filterSidebarOpen ? 'filter-sidebar-open' : 'filter-sidebar-closed'}`}
              style={{ marginTop: 'var(--top-nav-height)' }}
            >
              {/* Demo Banner - only show for demo users */}
              {!isAuthenticated && apiKey === 'demo' && (
                <div className="demo-banner">
                  <div className="demo-banner-content">
                    <div className="demo-banner-text">
                      <strong>Pay-as-you-go pricing: $0.02 per contact</strong>
                      <p>Sign in and add a payment method to get a $20 credit (1,000 contacts)</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method Banner - only show for authenticated users without payment method */}
              {(() => {
                const isAuthContextAuthenticated = apiKey && authUser;
                const shouldShow = isAuthContextAuthenticated && apiKey !== 'demo' && state.billingBalance?.has_payment_method === false && !authUser?.permissions?.includes('admin');
                console.log('🔧 Payment Banner Debug:', {
                  isFirebaseAuthenticated: isAuthenticated,
                  isAuthContextAuthenticated,
                  apiKey: apiKey?.substring(0, 8) + '...',
                  billingBalance: state.billingBalance,
                  hasPaymentMethod: state.billingBalance?.has_payment_method,
                  authUser: authUser,
                  authUserPermissions: authUser?.permissions,
                  isAdmin: authUser?.permissions?.includes('admin'),
                  shouldShow
                });
                
                return shouldShow ? (
                  <div className="payment-banner">
                    <div className="payment-banner-content">
                      <div className="payment-banner-text">
                        <strong>Pay-as-you-go pricing: $0.02 per contact</strong>
                        <p>Add a payment method to get a $20 credit (1,000 contacts)</p>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '1rem', 
                minHeight: 40 
              }}>
                <div className="page-breadcrumb" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  color: '#6b7280', 
                  fontSize: 15 
                }}>
                  {currentDataSource === 'people' ? (
                    <>
                      <Search size={16} />
                      <span>Search</span>
                      <ChevronRight size={14} />
                      <Users size={16} />
                      <span>People</span>
                      {showSearchLoading ? (
                        <span className="skeleton-text" />
                      ) : (
                        typeof state.totalResults === 'number' && (
                          <span className="breadcrumb-count-chip">
                            {state.totalResults.toLocaleString()}
                          </span>
                        )
                      )}
                    </>
                  ) : (
                    <>
                      <List size={16} />
                      <span>Lists</span>
                      <ChevronRight size={14} />
                      <BookOpen size={16} />
                      <span>Contacts</span>
                      {showSearchLoading ? (
                        <span className="skeleton-text" />
                      ) : (
                        typeof state.totalResults === 'number' && (
                          <span className="breadcrumb-count-chip">
                            {state.totalResults.toLocaleString()}
                          </span>
                        )
                      )}
                    </>
                  )}
                </div>

                {currentDataSource === 'contacts' ? (
                  <div className="action-buttons">
                    {/* Export button */}
                    {hasPermission('export') && (
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => { 
                          if (apiKey) exportSelectedContacts(apiKey, 'csv'); 
                        }} 
                        disabled={!allSelected && selectedCount === 0}
                      >
                        Export
                      </button>
                    )}

                    {/* Remove from List button */}
                    {state.navigationState.listId && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => { 
                          if (apiKey && state.navigationState.listId) {
                            removeFromListJobAsync(apiKey, state.navigationState.listId); 
                          }
                        }}
                        disabled={!allSelected && selectedCount === 0}
                        title="Remove selected contacts from this list (keeps contacts in your account)"
                      >
                        Remove from List
                      </button>
                    )}

                    {/* Delete button */}
                    {hasPermission('contacts') && (
                      <button
                        className="btn btn-danger"
                        onClick={() => { 
                          if (apiKey) deleteContactsJobAsync(apiKey); 
                        }}
                        disabled={!allSelected && selectedCount === 0}
                        title="Permanently delete selected contacts from your account (cannot be undone)"
                      >
                        Delete
                      </button>
                    )}
                    
                    {/* List selector */}
                    <div className="list-selector">
                      <label htmlFor="list-picker">
                        List:
                      </label>
                      <select
                        id="list-picker"
                        value={state.navigationState.listId || 'ALL'}
                        onChange={e => setNavigation({ 
                          view: 'list', 
                          listId: e.target.value === 'ALL' ? null : e.target.value 
                        })}
                        disabled={listPickerLoading}
                      >
                        <option value="ALL">All Contacts</option>
                        {listPickerLists.map((list: UserList) => (
                          <option key={list.list_id} value={list.list_id}>
                            {list.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Delete List button */}
                    {state.navigationState.listId && hasPermission('contacts') && (
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          const list = listPickerLists.find(l => l.list_id === state.navigationState.listId);
                          if (list && apiKey) {
                            deleteList(apiKey, list.list_id, list.name, () => {
                              setNavigation({ view: 'list', listId: null });
                              const event = new Event('lists-refresh');
                              window.dispatchEvent(event);
                            });
                          }
                        }}
                        disabled={listPickerLoading}
                        title={`Delete list "${listPickerLists.find(l => l.list_id === state.navigationState.listId)?.name}"`}
                      >
                        Delete List
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button 
                      className="button-primary" 
                      onClick={() => {
                        if (hasPermission('contacts')) {
                          setListSelectionModalOpen(true);
                        } else {
                          // Demo user - prompt to sign in
                          showToast({
                            type: 'info',
                            title: 'Sign In Required',
                            message: 'Please sign in to add contacts to lists. Click "Sign In" in the top right corner.',
                            duration: 6000
                          });
                        }
                      }} 
                      disabled={selectedCount === 0 && !allSelected}
                    >
                      {`Add ${allSelected ? 'All' : selectedCount} Contact${selectedCount !== 1 || allSelected ? 's' : ''}`}
                    </button>
                  </div>
                )}
              </div>

              <ErrorBoundary>
                <DataTable 
                  loading={showSearchLoading} 
                  currentListId={state.navigationState.listId}
                  initialLoadComplete={!isInitialLoad.current}
                />
              </ErrorBoundary>
              <ErrorBoundary>
                <Pagination loading={showSearchLoading} />
              </ErrorBoundary>
            </div>
          </>
        );
      case 'terms':
        console.log('🔍 Rendering terms view');
        return <Terms onBack={() => setView('main')} />;
      case 'privacy':
        console.log('🔍 Rendering privacy view');
        return <PrivacyPolicy onBack={() => setView('main')} />;
      case 'activity':
        console.log('🔍 Rendering activity view');
        return <Activity />;
      case 'admin':
        console.log('🔍 Rendering admin view');
        return <AdminPage />;
      case 'billing':
        console.log('🔍 Rendering billing view');
        return <AccountPage />;
    }
  }, [
    state.currentView,
    state.navigationState,
    state.totalResults,
    dispatch,
    hasPermission,
    filterSidebarOpen,
    showSearchLoading,
    currentDataSource,
    allSelected,
    selectedCount,
    apiKey,
    exportSelectedContacts,
    removeFromListJobAsync,
    deleteContactsJobAsync,
    setNavigation,
    listPickerLists,
    listPickerLoading,
    deleteList,
    setListSelectionModalOpen,
    setView
  ]);

  // OPTIMIZED: Early returns for different states
  console.log(`🔧 AppContent render - initialized: ${state.initialized}, apiConnected: ${state.apiConnected}, schemaError: ${state.schemaError}`);
  if (!state.initialized) {
    console.log('⏳ AppContent showing loading state - not initialized');
    return <LoadingState />;
  }

  // Only show error state if there's a connection/schema error
  if (!state.apiConnected || state.schemaError) {
    return (
      <ErrorState
        error={state.error}
        schemaError={state.schemaError}
        apiKey={apiKey}
        rateLimitInfo={rateLimitInfo}
        circuitBreakerState={circuitBreakerState}
      />
    );
  }

  const isPageView = ['terms', 'privacy', 'activity', 'admin', 'billing'].includes(state.currentView);

  return (
    <div className="app">
      <NotificationCenter />
      <ConfirmationDialog />

      <ErrorBoundary>
        <ListSelectionModal
          isOpen={listSelectionModalOpen}
          onClose={() => setListSelectionModalOpen(false)}
          selectAll={state.selection.selectAll}
          totalCount={state.totalResults}
          onConfirm={(listIds, selectAll, shouldRefreshLists) => {
            if (apiKey && hasPermission('contacts')) {
              if (state.searchState.dataSource === 'people') {
                unlockSelectedContacts(apiKey, listIds, selectAll);
              } else {
                addSelectedContactsToList(apiKey, listIds, selectAll);
              }
              if (shouldRefreshLists) {
                const event = new Event('lists-refresh');
                window.dispatchEvent(event);
              }
            } else {
              // Demo user or insufficient permissions - prompt to sign in
              showToast({
                type: 'info',
                title: 'Sign In Required',
                message: 'Please sign in to add contacts to lists. Click "Sign In" in the top right corner.',
                duration: 6000
              });
            }
            setListSelectionModalOpen(false);
          }}
          selectedCount={selectedCount}
        />
      </ErrorBoundary>



      <TopNav 
        onFilterToggle={() => setFilterSidebarOpen(!filterSidebarOpen)} 
        filterSidebarOpen={filterSidebarOpen} 
      />

      <main className={`app-body ${isPageView ? 'page-view' : ''}`}>
        {renderView()}
      </main>
    </div>
  );
});

AppContent.displayName = 'AppContent';

// OPTIMIZED: Main App component with better error boundaries and auto-demo login
const App: React.FC = () => {
  const { isAuthenticated, user, loading } = useFirebaseAuth();
  const [shouldShowDemo, setShouldShowDemo] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(false);
   const apiKey = user?.apiKey || null;

  console.log(`🔍 App Auth State - isAuthenticated: ${isAuthenticated}, user: ${user?.displayName || 'null'}, loading: ${loading}, apiKey: ${apiKey?.substring(0, 8) || 'null'}..., showLoginPage: ${showLoginPage}, shouldShowDemo: ${shouldShowDemo}`);

  // Debug: Track isAuthenticated changes specifically
  useEffect(() => {
    console.log(`🔧 App: isAuthenticated changed to: ${isAuthenticated}`);
  }, [isAuthenticated]);

  // Force re-check on admin login success
  useEffect(() => {
    const handleAdminLogin = () => {
      console.log('🔧 App: Received admin login success event, forcing state updates');
      // Force multiple state updates to trigger re-evaluation
      setShouldShowDemo(false);
      setShowLoginPage(false);
      // Clear any stored flags that might interfere
      localStorage.removeItem('show_login_page');
      localStorage.removeItem('needs_email_verification');
      
      // Force App component to re-evaluate by triggering a dummy state change
      setTimeout(() => {
        console.log('🔧 App: Secondary state update after admin login');
        setShowLoginPage(() => false);
      }, 50);
    };

    window.addEventListener('admin-login-success', handleAdminLogin);
    return () => window.removeEventListener('admin-login-success', handleAdminLogin);
  }, []);

  // Auto-login with demo account when not authenticated
  useEffect(() => {
    // Check if user explicitly wants to see login page
    const wantsLogin = localStorage.getItem('show_login_page') === 'true';
    // Check if user just signed up and needs to verify email
    const needsVerification = localStorage.getItem('needs_email_verification') === 'true';
    
    if (!loading && !isAuthenticated) {
      if (wantsLogin || needsVerification) {
        console.log('🔒 User requested login page or needs email verification');
        setShowLoginPage(true);
        setShouldShowDemo(false);
        // Clear only the show_login_page flag, let LoginPage handle verification flags
        localStorage.removeItem('show_login_page');
      } else {
        console.log('🎯 No authenticated user, enabling demo mode');
        setShouldShowDemo(true);
        setShowLoginPage(false);
      }
    } else if (isAuthenticated) {
      console.log('🎯 User is authenticated, hiding login page and demo mode');
      setShouldShowDemo(false);
      setShowLoginPage(false);
      // Clear any login flags - verification flags will be cleared by LoginPage
      localStorage.removeItem('show_login_page');
    }
  }, [loading, isAuthenticated]);

  // Show loading state while Firebase auth is initializing
  if (loading) {
    console.log('🔄 App showing loading state');
    return <LoadingState />;
  }

  // Show login page if user explicitly wants to login or demo logout
  if (!isAuthenticated && showLoginPage) {
    console.log('🔒 App showing login page - user requested login');
    return (
      <ErrorBoundary>
        <AuthProvider initialApiKey={apiKey}>
          <AppProvider>
            <div className="app">
              <NotificationCenter />
              <LoginPage />
            </div>
          </AppProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  // Use demo account when not authenticated
  if (!isAuthenticated && shouldShowDemo) {
    console.log('✅ App showing main content with demo user');
    
    // Demo API key from Elasticsearch
    const demoApiKey = 'demo';

    // Enhanced error boundary with reset keys for billing components
    const errorBoundaryResetKeys = [demoApiKey, 'demo-user'].filter(Boolean);
    
    return (
      <ErrorBoundary resetKeys={errorBoundaryResetKeys}>
        <AuthProvider initialApiKey={demoApiKey}>
          <AppProvider>
            <AppContent />
          </AppProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  // Fallback: show login page if something goes wrong
  if (!isAuthenticated) {
    console.log('🔒 App showing login page - fallback');
    return (
      <ErrorBoundary>
        <AuthProvider initialApiKey={apiKey}>
          <AppProvider>
            <div className="app">
              <NotificationCenter />
              <LoginPage />
            </div>
          </AppProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  console.log('✅ App showing main content - authenticated with Firebase');

  // Enhanced error boundary with reset keys for billing components
  const errorBoundaryResetKeys = [apiKey || '', user?.uid || ''].filter(Boolean);
  
  return (
    <ErrorBoundary resetKeys={errorBoundaryResetKeys}>
      <AuthProvider initialApiKey={apiKey}>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;